// src/checklist/blanka.js — „Работна бланка": втори (табличен) изглед в Чек лист.
//
// Матрица „фирма × задача" по месеци. Логиката (кои колони/клетки важат) е на
// сървъра (services/blanka.js) — тук се рисува и се редактира клетка.

import { el, clear, $, field, input, pressable } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, searchInput, tabBar } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { state } from '../shared/state.js';

// Огледало на статусите от services/blanka.js (етикет, tag-цвят, отметка).
const STATUS = {
  pending: ['Чака', 'gray', ''],
  submitted: ['Подадено', 'amber', '✓'],
  verified: ['Проверено', 'green', '✓✓'],
  notdue: ['Не подлежи', 'gray', '—'],
  notsubmitted: ['Не подадено', 'red', '✗'],
};
const STATUS_ORDER = ['pending', 'submitted', 'verified', 'notdue', 'notsubmitted'];
const MONTHS = ['', 'Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни', 'Юли', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'];

// Филтри (пазят се между рендерите).
// По подразбиране се показва ПРЕДХОДНИЯТ месец (бланката се работи с месец
// назад: през юли се попълва юни). Януари → декември на предходната година.
const now = new Date();
const _prev = defaultPeriod(now);
let _tab = 'monthly';
let _year = _prev.year;
let _month = _prev.month;

// Изнесена за тестовете: коя (година, месец) е по подразбиране за дата d.
export function defaultPeriod(d) {
  const m = d.getMonth() + 1; // 1..12 (текущият)
  return m === 1 ? { year: d.getFullYear() - 1, month: 12 } : { year: d.getFullYear(), month: m - 1 };
}
let _accId = null;   // избран счетоводител (само админ; null = аз/всички)
let _sig = '';       // филтър по подпис: '' | 'БВ' | 'ДВ'
let _search = '';

// Обяснение защо клетката е заключена (за да не „не се случва нищо").
function cellReason(col, firm) {
  if (col.id === 'dds' && (firm.vat_period || 'none') === 'none') return 'Фирмата е „Нерегистриран по ДДС". За да въвеждаш ДДС, смени ДДС периода от реда на фирмата (само админ).';
  if (col.id === 'dds' && firm.vat_period === 'quarterly') return 'ДДС за тази фирма е тримесечно — попълва се в януари/април/юли/октомври.';
  if (col.id === 'advance') return 'Тази фирма няма авансов данък.';
  return 'Тази клетка не важи за фирмата.';
}

const isAdmin = () => state().user && state().user.role === 'admin';

export async function render(page) {
  const wrap = el('div', {});
  page.appendChild(wrap);
  await draw(wrap);
}

async function draw(wrap) {
  // Пазим докъде е превъртяно (и страницата, и матрицата хоризонтално) —
  // иначе всеки запис на клетка връща потребителя най-горе на списъка.
  const prevBox = wrap.querySelector('.blanka-scrollbox');
  const pageEl = $('#page');
  const keep = {
    x: prevBox ? prevBox.scrollLeft : 0,
    page: pageEl ? pageEl.scrollTop : 0,
    win: window.scrollY || 0,
  };

  clear(wrap);
  wrap.appendChild(el('div', { class: 'cal-hint', text: 'Зареждане…' }));

  let data;
  try {
    let url = '/blanka/matrix?tab=' + _tab + '&year=' + _year + (_tab === 'monthly' ? '&month=' + _month : '');
    if (isAdmin() && _accId) url += '&accountant_user_id=' + _accId;
    data = await api(url);
  } catch (e) { clear(wrap); wrap.appendChild(el('div', { class: 'cal-hint', text: e.message })); return; }

  clear(wrap);
  const panel = el('div', { class: 'panel' });

  // ---- Лента с филтри ----
  const yearSel = el('select', { 'aria-label': 'Година', onChange: (e) => { _year = Number(e.target.value); draw(wrap); } },
    ...[now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2]
      .map((y) => el('option', { value: y, text: String(y), selected: y === _year })));
  const monthSel = el('select', { 'aria-label': 'Месец', onChange: (e) => { _month = Number(e.target.value); draw(wrap); } },
    ...MONTHS.slice(1).map((m, i) => el('option', { value: i + 1, text: m, selected: (i + 1) === _month })));

  const sigSel = el('select', { 'aria-label': 'Подпис', onChange: (e) => { _sig = e.target.value; drawTable(); } },
    el('option', { value: '', text: 'Всички подписи', selected: !_sig }),
    el('option', { value: 'БВ', text: 'Подпис БВ', selected: _sig === 'БВ' }),
    el('option', { value: 'ДВ', text: 'Подпис ДВ', selected: _sig === 'ДВ' }));

  const filters = el('div', { class: 'filter-bar' },
    tabBar([{ id: 'monthly', label: 'Месечни' }, { id: 'annual', label: 'Годишни' }],
      _tab, (id) => { _tab = id; draw(wrap); }, 'Месечни или годишни задължения', { inline: true }),
    _tab === 'monthly' ? monthSel : null,
    yearSel,
    sigSel
  );

  // Счетоводител (само админ).
  if (isAdmin()) {
    const accSel = el('select', { 'aria-label': 'Счетоводител', onChange: (e) => { _accId = e.target.value ? Number(e.target.value) : null; draw(wrap); } },
      el('option', { value: '', text: 'Всички счетоводители', selected: !_accId }));
    filters.appendChild(accSel);
    api('/auth/users').then((users) => {
      // Счетоводители са само служебните потребители — клиентите никога.
      users.filter((u) => u.role !== 'client').forEach((u) => accSel.appendChild(el('option', { value: u.id, text: u.name, selected: Number(u.id) === _accId })));
    }).catch(() => {});
  }

  // value: _search се пази между рендерите — полето трябва да го отразява,
  // иначе филтърът действа, а полето изглежда празно (разсинхрон).
  filters.appendChild(searchInput({ placeholder: 'Търси фирма…', ariaLabel: 'Търсене на фирма', value: _search, onSearch: (v) => { _search = v; drawTable(); } }));

  const s = data.summary || { done: 0, total: 0 };
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Работна бланка' }),
    el('span', { class: 'tag ' + (s.done >= s.total && s.total ? 'green' : 'amber'), style: 'margin-left:auto', text: 'подадено/проверено ' + s.done + ' от ' + s.total }),
    (isAdmin() && _tab === 'annual') ? el('button', { class: 'btn sm', text: '+ Нова колона', onClick: () => columnForm(wrap, data.firms) }) : null,
    isAdmin() ? el('button', { class: 'btn sm ghost', text: '+ Фирма', onClick: () => firmForm(wrap, null) }) : null,
    isAdmin() ? el('button', { class: 'btn sm ghost', text: '⤓ Импорт от Excel', onClick: () => importForm(wrap) }) : null
  ));
  panel.appendChild(filters);

  const tableBox = el('div', { class: 'blanka-scrollbox', style: 'overflow-x:auto' });
  panel.appendChild(tableBox);
  wrap.appendChild(panel);

  const naSet = new Set(data.na || []);
  const forced = data.forced || {};
  const entryMap = {};
  (data.entries || []).forEach((e) => { entryMap[e.firm_id + ':' + e.task] = e; });

  function drawTable() {
    clear(tableBox);
    const q = _search.trim().toLowerCase();
    const firms = (data.firms || []).filter((f) =>
      (!q || String(f.name || '').toLowerCase().includes(q) || String(f.eik || '').includes(q))
      && (!_sig || f.signature === _sig));
    if (!(data.firms || []).length) {
      tableBox.appendChild(emptyState('▦', isAdmin() ? 'Още няма фирми в бланката. Добавете първата с „+ Фирма".' : 'Още нямате назначени фирми.'));
      return;
    }
    if (!firms.length) { tableBox.appendChild(emptyState('▦', 'Няма фирма по това търсене.')); return; }

    const admin = isAdmin();
    const thead = el('tr', {},
      el('th', { text: 'Фирма' }),
      el('th', { class: 'hide-sm', text: 'Работи го' }),
      el('th', { class: 'hide-sm', text: 'Подпис' }));
    data.columns.forEach((c) => {
      // Динамичните колони: заглавието е кликаемо за админ (редакция — име,
      // кои фирми, скриване). Вградените остават само надпис.
      const editable = admin && !c.builtin;
      const th = el('th', editable
        ? { class: 'blanka-colhead', style: 'text-align:center;cursor:pointer', title: 'Кликни за редакция (админ)' }
        : { style: 'text-align:center' }, c.label + (editable ? ' ✎' : ''));
      if (editable) pressable(th, () => columnForm(wrap, data.firms, c), 'Редактирай колоната ' + c.label);
      thead.appendChild(th);
    });
    const tb = el('tbody');
    firms.forEach((f) => {
      // Име на фирмата — за админ е кликаемо за редакция (човек, ДДС период…).
      const nameCell = el('td', admin ? { class: 'blanka-firm', title: 'Кликни за редакция (админ)' } : {},
        el('strong', { text: f.name }),
        el('div', { style: 'font-size:12px;color:var(--muted)', text: f.eik || '' }));
      if (admin) pressable(nameCell, () => firmForm(wrap, f), 'Редактирай фирмата ' + f.name);
      const tr = el('tr', {}, nameCell,
        el('td', { class: 'hide-sm', text: f.accountant_name || '—' }),
        el('td', { class: 'hide-sm', text: f.signature || '' }));
      data.columns.forEach((col) => {
        const key = f.id + ':' + col.id;
        // Неприложима клетка: клик обяснява ЗАЩО (вместо да не се случва нищо).
        if (naSet.has(key)) {
          tr.appendChild(pressable(el('td', { class: 'blanka-cell na', text: '·' }), () => toast(cellReason(col, f)), f.name + ', ' + col.label + ': не се отнася'));
          return;
        }
        const forcedSt = forced[key];
        const e = entryMap[key];
        const st = forcedSt || (e && e.status) || 'pending';
        const info = STATUS[st] || STATUS.pending;
        const amt = (col.id === 'dds' && e && e.amount != null && e.amount !== '') ? money(Number(e.amount), 'EUR') : '';
        const cell = el('td', {
          class: 'blanka-cell ' + info[1] + (forcedSt ? ' forced' : ''),
          title: forcedSt ? cellReason(col, f) : (info[0] + (e && e.updated_by_name ? ' · ' + e.updated_by_name : '')),
        }, el('span', { class: 'blanka-mark', text: info[2] || '·' }), amt ? el('span', { class: 'blanka-amt', text: amt }) : null);
        // Заключена клетка (напр. ДДС за нерегистриран): обяснява, вместо тишина.
        pressable(cell, forcedSt ? () => toast(cellReason(col, f)) : () => cellForm(wrap, f, col, e),
          f.name + ', ' + col.label + ': ' + info[0] + (amt ? ', ' + amt : ''));
        tr.appendChild(cell);
      });
      tb.appendChild(tr);
    });
    tableBox.appendChild(el('table', { class: 'blanka-table' }, el('thead', {}, thead), tb));
  }
  drawTable();

  // Връщаме превъртането там, където беше преди преначертаването.
  tableBox.scrollLeft = keep.x;
  if (pageEl) pageEl.scrollTop = keep.page;
  if (keep.win) window.scrollTo(0, keep.win);
}

// ---- Редактор на клетка ---------------------------------------------------

function cellForm(wrap, firm, col, existing) {
  const e = existing || {};
  let chosen = e.status || 'pending';
  const isDds = col.id === 'dds';

  const statusBtns = el('div', { style: 'display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px' });
  const redraw = () => {
    clear(statusBtns);
    STATUS_ORDER.forEach((s) => {
      const info = STATUS[s];
      statusBtns.appendChild(el('button', {
        type: 'button', class: 'btn sm' + (chosen === s ? '' : ' ghost'),
        onClick: () => { chosen = s; redraw(); },
      }, el('span', { class: 'tag ' + info[1], style: 'margin-right:6px', text: info[2] || '•' }), info[0]));
    });
  };
  redraw();

  const amount = input({ type: 'number', step: '0.01', value: (e.amount != null ? e.amount : ''), placeholder: 'сума за внасяне/възстановяване' });
  const note = el('textarea', { rows: 2, placeholder: 'Бележка (по избор)' }, e.note || '');

  const body = el('div', {},
    el('p', { style: 'margin:0 0 10px;color:var(--muted);font-size:13px', text: firm.name + ' — ' + col.label + (_tab === 'monthly' ? ' · ' + MONTHS[_month] + ' ' + _year : ' · ' + _year) }),
    el('div', { class: 'field' }, el('label', { text: 'Статус' }), statusBtns),
    isDds ? field('Сума (EUR)', amount) : null,
    field('Бележка', note)
  );

  const save = async () => {
    try {
      await api('/blanka/entry', {
        method: 'POST',
        body: JSON.stringify({
          firm_id: firm.id, tab: _tab, year: _year, month: _tab === 'monthly' ? _month : 0,
          task: col.id, status: chosen,
          amount: isDds ? (amount.value === '' ? null : Number(amount.value)) : null,
          note: note.value.trim(),
        }),
      });
      closeModal(); toast('Записано.'); draw(wrap);
    } catch (err) { toast(err.message, true); }
  };

  openModal({
    title: 'Статус на клетка',
    body,
    foot: el('div', {},
      el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
      el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: save })),
  });
}

// ---- Нова динамична колона (само админ, таб Годишни) ----------------------

function columnForm(wrap, firms, existing) {
  const it = existing || {};
  const preIds = new Set((it.firm_ids || []).map(Number));
  const label = input({ value: it.label || '', placeholder: 'напр. Декларация по чл. X' });
  const appliesAll = el('input', { type: 'radio', name: 'bl-applies', checked: it.applies_to !== 'flagged' });
  const appliesFlagged = el('input', { type: 'radio', name: 'bl-applies', checked: it.applies_to === 'flagged' });
  const firmChecks = [];
  const firmBox = el('div', { style: (it.applies_to === 'flagged' ? '' : 'display:none;') + 'max-height:180px;overflow:auto;border:1px solid var(--border);border-radius:8px;padding:8px;margin-top:6px' });
  (firms || []).forEach((f) => {
    const cb = el('input', { type: 'checkbox', value: f.id, checked: preIds.has(Number(f.id)) });
    firmChecks.push(cb);
    firmBox.appendChild(el('label', { style: 'display:flex;gap:6px;align-items:center;font-size:14px;margin-bottom:4px' }, cb, f.name));
  });
  appliesAll.addEventListener('change', () => { firmBox.style.display = 'none'; });
  appliesFlagged.addEventListener('change', () => { firmBox.style.display = ''; });

  const body = el('div', {},
    field('Име на колоната', label),
    el('div', { class: 'field' }, el('label', { text: 'За кои фирми важи (добавяне/махане — само админ)' }),
      el('label', { style: 'display:flex;gap:6px;align-items:center;margin-bottom:4px' }, appliesAll, 'Всички фирми'),
      el('label', { style: 'display:flex;gap:6px;align-items:center' }, appliesFlagged, 'Само маркираните'),
      firmBox)
  );

  const save = async () => {
    if (!label.value.trim()) { toast('Въведете име на колоната.', true); return; }
    const applies_to = appliesFlagged.checked ? 'flagged' : 'all';
    const firm_ids = applies_to === 'flagged' ? firmChecks.filter((c) => c.checked).map((c) => Number(c.value)) : [];
    try {
      if (it.id) await api('/blanka/columns/' + it.id, { method: 'PUT', body: JSON.stringify({ label: label.value.trim(), applies_to, firm_ids }) });
      else await api('/blanka/columns', { method: 'POST', body: JSON.stringify({ tab: 'annual', label: label.value.trim(), applies_to, firm_ids }) });
      closeModal(); toast(it.id ? 'Колоната е обновена.' : 'Колоната е добавена.'); draw(wrap);
    } catch (e) { toast(e.message, true); }
  };

  const foot = el('div', {},
    it.id ? el('button', {
      class: 'btn sm danger', text: 'Скрий колоната', style: 'margin-right:auto',
      onClick: async () => {
        if (!(await confirmDialog('Да скрия ли колоната? Попълнените клетки се пазят и се връщат при показване.', { okText: 'Скрий' }))) return;
        try { await api('/blanka/columns/' + it.id, { method: 'PUT', body: JSON.stringify({ active: false }) }); closeModal(); toast('Колоната е скрита.'); draw(wrap); }
        catch (e) { toast(e.message, true); }
      },
    }) : null,
    el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
    el('button', { class: 'btn', text: it.id ? 'Запази' : 'Добави', style: 'margin-left:8px', onClick: save })
  );

  openModal({ title: it.id ? 'Редакция на колона' : 'Нова колона (Годишни)', body, foot });
}

// ---- Импорт от стария Excel (само админ) ----------------------------------

function importForm(wrap) {
  const file = el('input', { type: 'file', accept: '.csv,text/csv' });
  const yearInp = input({ type: 'number', value: new Date().getFullYear(), style: 'max-width:120px' });
  const info = el('div', { class: 'cal-hint', text: 'Изберете CSV файл (експорт от бланката: Файл → Запази като CSV). Кирилицата се разчита автоматично.' });
  const result = el('div', {});
  let csvText = '';

  // Чете файла и го декодира от Windows-1251 (стандартът на Excel у нас).
  file.addEventListener('change', () => {
    const f = file.files && file.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try { csvText = new TextDecoder('windows-1251').decode(new Uint8Array(reader.result)); info.textContent = 'Файл: ' + f.name + ' (' + Math.round(f.size / 1024) + ' KB). Натиснете „Прегледай".'; }
      catch (e) { info.textContent = 'Грешка при четене: ' + e.message; }
    };
    reader.readAsArrayBuffer(f);
  });

  const preview = async () => {
    if (!csvText) { toast('Първо изберете файл.', true); return; }
    clear(result); result.appendChild(el('div', { class: 'cal-hint', text: 'Обработвам…' }));
    try {
      const p = await api('/blanka/import/preview', { method: 'POST', body: JSON.stringify({ csv: csvText, year: Number(yearInp.value) }) });
      clear(result);
      result.appendChild(el('div', { style: 'font-weight:700;margin-bottom:6px', text: 'Ще се внесат: ' + p.firms + ' фирми, ' + p.cells + ' клетки (' + p.columns + ' колони).' }));
      if (p.existing) result.appendChild(el('div', { class: 'tag red', style: 'display:inline-block;margin-bottom:8px', text: 'ВНИМАНИЕ: вече има ' + p.existing + ' фирми — вносът ще ги ДУБЛИРА. Първо изчистете старите.' }));
      if (p.unmatched && p.unmatched.length) result.appendChild(el('div', { class: 'cal-hint', text: '⚠ Несвързани счетоводители (фирмите им ще са без назначение — ще ги видите само вие): ' + p.unmatched.join(', ') }));
      if (p.ambiguous && p.ambiguous.length) result.appendChild(el('div', { class: 'cal-hint', text: '⚠ Двусмислени имена (двама+ потребители еднакви — оставени без назначение): ' + p.ambiguous.join(', ') }));
      const tb = el('tbody');
      p.sample.forEach((f) => tb.appendChild(el('tr', {},
        el('td', { text: f.name }), el('td', { text: f.eik || '—' }),
        el('td', {}, f.matched ? el('span', { class: 'tag green', text: f.accName }) : el('span', { class: 'tag red', text: f.accName + ' (несвързан)' })),
        el('td', { text: f.signature }), el('td', { text: f.vat_period }), el('td', { text: f.cells + ' клетки' }))));
      result.appendChild(el('div', { style: 'font-size:13px;color:var(--muted);margin:8px 0 4px', text: 'Мостра (първите ' + p.sample.length + ' фирми):' }));
      result.appendChild(el('table', {}, el('thead', {}, el('tr', {}, el('th', { text: 'Фирма' }), el('th', { text: 'ЕИК' }), el('th', { text: 'Счетоводител' }), el('th', { text: 'Подпис' }), el('th', { text: 'ДДС' }), el('th', { text: 'Клетки' }))), tb));
      applyBtn.style.display = '';
      applyBtn.dataset.existing = p.existing || 0;
    } catch (e) { clear(result); result.appendChild(el('div', { class: 'cal-hint', text: e.message })); }
  };

  const doApply = async (force) => {
    try {
      const r = await api('/blanka/import/apply', { method: 'POST', body: JSON.stringify({ csv: csvText, year: Number(yearInp.value), force: force === true }) });
      closeModal(); toast('Внесени ' + r.firms + ' фирми, ' + r.cells + ' клетки.'); draw(wrap);
    } catch (e) {
      if (/дублира/.test(e.message)) { if (await confirmDialog(e.message + ' Наистина ли да внеса пак?', { okText: 'Внеси въпреки това', danger: true })) return doApply(true); return; }
      toast(e.message, true);
    }
  };

  const applyBtn = el('button', { class: 'btn', text: 'Внеси', style: 'margin-left:8px;display:none', onClick: () => doApply(false) });

  openModal({
    title: 'Импорт от Excel', wide: true,
    body: el('div', {}, el('div', { class: 'field-row' }, field('CSV файл', file), field('Година', yearInp)), info, result),
    foot: el('div', {},
      el('button', { class: 'btn ghost', text: 'Затвори', onClick: closeModal }),
      el('button', { class: 'btn sm', text: 'Прегледай', onClick: preview }),
      applyBtn),
  });
}

// ---- Фирма в бланката (само админ) ----------------------------------------

function firmForm(wrap, existing) {
  const it = existing || {};
  const f = {
    name: input({ value: it.name || '', placeholder: 'Име на фирмата' }),
    eik: input({ value: it.eik || '', placeholder: 'ЕИК' }),
    email: input({ value: it.email || '', placeholder: 'имейл' }),
    accountant: el('select', {}),
    signature: el('select', {}, el('option', { value: 'БВ', text: 'БВ', selected: it.signature === 'БВ' }), el('option', { value: 'ДВ', text: 'ДВ', selected: it.signature === 'ДВ' })),
    vat: el('select', {},
      el('option', { value: 'monthly', text: 'ДДС месечно', selected: it.vat_period === 'monthly' }),
      el('option', { value: 'quarterly', text: 'ДДС тримесечно', selected: it.vat_period === 'quarterly' }),
      el('option', { value: 'none', text: 'Нерегистриран по ДДС', selected: !it.vat_period || it.vat_period === 'none' })),
    advance: el('input', { type: 'checkbox', checked: !!it.has_advance_tax }),
  };
  api('/auth/users').then((users) => {
    // Клиент никога не е счетоводител — не се предлага в избора.
    const staff = users.filter((u) => u.role !== 'client');
    staff.forEach((u) => f.accountant.appendChild(el('option', { value: u.id, text: u.name, selected: Number(u.id) === Number(it.accountant_user_id) })));
    // ЗАВАРЕН запис с текущ счетоводител ИЗВЪН списъка (напр. клиент от
    // преди правилото): пазим го като изрична, избрана опция — иначе
    // браузърът тихо избира първия от списъка и „Запази" преназначава
    // фирмата без админът да е искал това.
    if (it.accountant_user_id && !staff.some((u) => Number(u.id) === Number(it.accountant_user_id))) {
      const legacy = users.find((u) => Number(u.id) === Number(it.accountant_user_id));
      f.accountant.appendChild(el('option', {
        value: it.accountant_user_id, selected: true,
        text: ((legacy && legacy.name) || ('потребител №' + it.accountant_user_id)) + ' (заварен — сменете)',
      }));
    }
  }).catch(() => {});

  const body = el('div', {},
    field('Име', f.name),
    el('div', { class: 'field-row' }, field('ЕИК', f.eik), field('Имейл', f.email)),
    el('div', { class: 'field-row' }, field('Счетоводител', f.accountant), field('Подпис', f.signature)),
    el('div', { class: 'field-row' }, field('ДДС период', f.vat),
      el('div', { class: 'field' }, el('label', { text: 'Авансов данък' }), el('label', { style: 'display:flex;gap:6px;align-items:center' }, f.advance, 'Има авансов данък')))
  );

  const save = async () => {
    const p = { name: f.name.value.trim(), eik: f.eik.value.trim(), email: f.email.value.trim(), accountant_user_id: Number(f.accountant.value) || null, signature: f.signature.value, vat_period: f.vat.value, has_advance_tax: f.advance.checked };
    if (!p.name) { toast('Въведете име.', true); return; }
    if (!p.accountant_user_id) { toast('Изберете счетоводител.', true); return; }
    try {
      if (it.id) await api('/blanka/firms/' + it.id, { method: 'PUT', body: JSON.stringify(p) });
      else await api('/blanka/firms', { method: 'POST', body: JSON.stringify(p) });
      closeModal(); toast('Записано.'); draw(wrap);
    } catch (e) { toast(e.message, true); }
  };

  openModal({
    title: it.id ? 'Фирма в бланката' : 'Нова фирма', body, wide: true,
    foot: el('div', {},
      // Триене (само админ — формата е достъпна само за админ): фирмата се
      // маха от бланката, но попълнените ѝ клетки СЕ ПАЗЯТ (меко изтриване).
      it.id ? el('button', {
        class: 'btn sm danger', text: 'Изтрий от бланката', style: 'margin-right:auto',
        onClick: async () => {
          if (!(await confirmDialog('Да махна ли „' + (it.name || '') + '" от бланката? Попълнените клетки се пазят и фирмата може да се върне после.', { okText: 'Изтрий', danger: true }))) return;
          try { await api('/blanka/firms/' + it.id, { method: 'DELETE' }); closeModal(); toast('Фирмата е махната от бланката.'); draw(wrap); }
          catch (e) { toast(e.message, true); }
        },
      }) : null,
      el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
      el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: save })),
  });
}
